import { useTranslation } from "react-i18next";import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Clock, MessageSquare, Paperclip, CheckSquare, AlertTriangle } from 'lucide-react';

const PRIORITY_COLORS = {
  low: 'border-l-green-500',
  medium: 'border-l-yellow-500',
  high: 'border-l-orange-500',
  urgent: 'border-l-red-500'
};

export default function KanbanCard({ card, listId, onClick, isDragging: isDraggingProp }) {const { t } = useTranslation("eztask");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging
  } = useSortable({
    id: card.card_id,
    data: { type: 'card', card, listId }
  });

  const isDragging = isDraggingProp || isSortableDragging;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const now = new Date();
  const twoDaysFromNow = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);
  const isOverdue = card.due_date && !card.due_complete && new Date(card.due_date) < now;
  const isDueSoon = card.due_date && !card.due_complete && !isOverdue &&
  new Date(card.due_date) < twoDaysFromNow;

  const hasChecklist = card.checklist_progress?.total > 0;
  const checklistComplete = hasChecklist && card.checklist_progress.complete === card.checklist_progress.total;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`bg-slate-900 rounded-lg shadow-lg cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all border-l-4 ${PRIORITY_COLORS[card.priority] || 'border-l-slate-600'}`}
      data-testid={`kanban-card-${card.card_id}`}>
      
      {/* Cover Image */}
      {card.cover_image &&
      <div
        className="h-32 rounded-t-lg bg-cover bg-center"
        style={{ backgroundImage: `url(${card.cover_image})` }} />

      }
      
      {/* Cover Color */}
      {!card.cover_image && card.cover_color &&
      <div className="h-8 rounded-t-lg" style={{ backgroundColor: card.cover_color }} />
      }

      <div className="p-3">
        {/* Labels */}
        {card.labels?.length > 0 &&
        <div className="flex flex-wrap gap-1 mb-2">
            {card.labels.map((label) =>
          <span
            key={label.id}
            className="h-2 w-10 rounded-full"
            style={{ backgroundColor: label.color }}
            title={label.name || label.color} />

          )}
          </div>
        }

        {/* Title */}
        <h4 className="text-sm font-medium text-white mb-2">{card.title}</h4>

        {/* Description Preview */}
        {card.description &&
        <p className="text-xs text-slate-400 mb-2 line-clamp-2">{card.description}</p>
        }

        {/* Metadata */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {/* Due Date */}
          {card.due_date &&
          <span className={`flex items-center gap-1 px-2 py-0.5 rounded ${
          card.due_complete ? 'bg-green-500/20 text-green-400' :
          isOverdue ? 'bg-red-500/20 text-red-400' :
          isDueSoon ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-slate-700'}`
          }>
              {isOverdue && !card.due_complete && <AlertTriangle className="w-3 h-3" />}
              <Clock className="w-3 h-3" />
              {new Date(card.due_date).toLocaleDateString()}
            </span>
          }

          {/* Checklist Progress */}
          {hasChecklist &&
          <span className={`flex items-center gap-1 ${checklistComplete ? 'text-green-400' : ''}`}>
              <CheckSquare className="w-3 h-3" />
              {card.checklist_progress.complete}/{card.checklist_progress.total}
            </span>
          }

          {/* Comment Count */}
          {card.comment_count > 0 &&
          <span className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              {card.comment_count}
            </span>
          }

          {/* Attachment Count */}
          {card.attachment_count > 0 &&
          <span className="flex items-center gap-1">
              <Paperclip className="w-3 h-3" />
              {card.attachment_count}
            </span>
          }

          {/* Assignees */}
          {card.member_ids?.length > 0 &&
          <div className="flex -space-x-1 ml-auto">
              {card.member_ids.slice(0, 3).map((memberId, index) =>
            <div
              key={memberId}
              className="w-6 h-6 rounded-full bg-blue-500 border border-slate-900 flex items-center justify-center text-white text-xs">
              
                  {index + 1}
                </div>
            )}
              {card.member_ids.length > 3 &&
            <div className="w-6 h-6 rounded-full bg-slate-600 border border-slate-900 flex items-center justify-center text-white text-xs">
                  +{card.member_ids.length - 3}
                </div>
            }
            </div>
          }
        </div>
      </div>
    </div>);

}